// Chart.js 2.x — no registration needed

export const SERIES_PAL = ['#1565c0','#e65100','#00897b','#ad1457','#6a1b9a','#00838f','#558b2f','#f57f17','#4e342e','#37474f'];

export const TECH_COLOR = { '5G': '#00897b', '4G': '#1565c0', '3G': '#e65100', '2G': '#ad1457' };
export const TECH_BADGE = {
	'5G': { bg: '#e0f2f1', color: '#00695c', border: '#80cbc4' },
	'4G': { bg: '#e3f2fd', color: '#0d47a1', border: '#90caf9' },
	'3G': { bg: '#fff3e0', color: '#bf360c', border: '#ffcc80' },
	'2G': { bg: '#fce4ec', color: '#880e4f', border: '#f48fb1' },
};

export const PHASE_COLOR    = 'rgba(21,101,192,0.5)';
export const ROLLBACK_COLOR = 'rgba(198,40,40,0.6)';

// Golden angle distribution — maximises visual separation between series
export function getSeriesColor(entityId, index) {
	if (!entityId || entityId === 'Activity') return SERIES_PAL[index % SERIES_PAL.length];
	const hue        = (index * 137.508) % 360;
	const lightness  = 38 + (index % 3) * 8;
	const saturation = 70 - (index % 2) * 10;
	return `hsl(${hue.toFixed(0)}, ${saturation}%, ${lightness}%)`;
}

// Returns { datasets, labels }
// When selectedEntities is non-empty, filters to only those series
// so Chart.js auto-scales Y axis to the visible data
export function buildDatasets(series = [], selectedEntities = []) {
	const hasSelection = Array.isArray(selectedEntities) && selectedEntities.length > 0;

	// Filter to selected series only — Y axis will auto-adjust
	const visible = hasSelection
		? series.filter(s => selectedEntities.includes((s.entityId ?? '').trim()))
		: series;

	// Labels = dates from first visible series (all series share same dates)
	const labels = (visible[0] ?? series[0])?.dates ?? [];

	const datasets = visible.map(s => {
		// Use index in FULL series for consistent colour across selections
		const originalIdx = series.findIndex(x => x.entityId === s.entityId);
		const color = getSeriesColor(s.entityId, originalIdx >= 0 ? originalIdx : 0);
		return {
			label:                     s.entityId,
			data:                      s.values,
			borderColor:               color,
			borderWidth:               1.8,
			pointRadius:               0,
			pointHoverRadius:          4,
			pointHoverBackgroundColor: color,
			fill:                      false,
			lineTension:               0,
		};
	});

	return { datasets, labels };
}

export function buildOptions(phases = [], labels = [], unit = '') {
	const annotations = phases.map(p => {
		const phaseDate = p.date ? p.date.slice(0, 10) : null;
		if (!phaseDate) return null;
		const xIdx = labels.findIndex(l => l.slice(0, 10) === phaseDate);
		if (xIdx === -1) return null;
		return {
			type:        'line',
			mode:        'vertical',
			scaleID:     'x-axis-0',
			value:       labels[xIdx],
			borderColor: p.isRollback == 1 ? ROLLBACK_COLOR : PHASE_COLOR,
			borderWidth: p.isRollback == 1 ? 2 : 1.5,
			borderDash:  p.isRollback == 1 ? [4, 3] : [],
			label: {
				enabled:         true,
				content:         p.label,
				position:        'top',
				backgroundColor: p.isRollback == 1 ? 'rgba(198,40,40,0.75)' : 'rgba(21,101,192,0.75)',
				fontColor:       '#fff',
				fontSize:        9,
				fontFamily:      'IBM Plex Mono, monospace',
				xPadding:        4,
				yPadding:        3,
				cornerRadius:    3,
			},
		};
	}).filter(Boolean);

	return {
		responsive:          true,
		maintainAspectRatio: false,
		animation:           { duration: 400 },
		legend:              { display: false },
		tooltips:            { enabled: false },
		annotation: annotations.length ? { annotations } : undefined,
		scales: {
			xAxes: [{
				gridLines: { color: 'rgba(0,0,0,.04)', lineWidth: .5 },
				ticks: { fontColor: 'rgba(0,0,0,.3)', fontFamily: 'IBM Plex Mono, monospace', fontSize: 8, maxTicksLimit: 6, maxRotation: 0 },
			}],
			yAxes: [{
				gridLines: { color: 'rgba(0,0,0,.04)', lineWidth: .5 },
				ticks: {
					fontColor: 'rgba(0,0,0,.6)', fontFamily: 'IBM Plex Mono, monospace', fontSize: 8, maxTicksLimit: 4,
					callback: (value) => {
						const n = typeof value === 'number' ? (value >= 1000 ? (value/1000).toFixed(1)+'K' : value % 1 === 0 ? value : value.toFixed(2)) : value;
						return unit ? `${n} ${unit}` : n;
					}
				},

			}],
		},
	};
}

export function extendDateRange(dateFrom, dateTo) {
	const pad = (d) => d.toISOString().slice(0, 10);
	const from = new Date(dateFrom); from.setDate(from.getDate() - 7);
	const to   = new Date(dateTo);   to.setDate(to.getDate() + 7);
	return { dateFrom: pad(from), dateTo: pad(to) };
}